import {
  CustomData,
  DataManager,
  KameleoonData,
  KameleoonDataType,
} from 'src/kameleoonData';
import { Requester } from 'src/requester';
import { VariationConfiguration } from 'src/variationConfiguration';
import { PendingDataType, TrackerParametersType } from './types';
import { TrackingStatus } from 'src/types';
import { BaseVariationDataType } from 'src/variationConfiguration/types';
import { BodyProvider } from './bodyProvider';
import { IStorage, TrackingDataType } from 'src/storage';
import { KameleoonLogger } from 'src/logging';
import { ScheduledVisitorsType } from 'src/storage/types';
import { StaticData } from 'src/kameleoonData/staticData';
import { ClientConfiguration } from 'src/clientConfiguration';

export interface ITracker {
  scheduleVisitor: (visitorCode: string, isConsentProvided: boolean) => void;
}

export class Tracker implements ITracker {
  private dataManager: DataManager;
  private variationConfiguration: VariationConfiguration;
  private requester: Requester;
  private bodyProvider: BodyProvider;
  private intervalId: NodeJS.Timeout | null = null;
  private trackingStorage: IStorage<TrackingDataType>;
  private clientConfiguration: ClientConfiguration;

  constructor({
    dataManager,
    trackingStorage,
    variationConfiguration,
    trackingInterval,
    requester,
    prng,
    clientConfiguration,
  }: TrackerParametersType) {
    KameleoonLogger.debug`CALL: new Tracker(dataManager, trackingStorage, variationConfiguration, trackingInterval: ${trackingInterval}, requester, prng, clientConfiguration)`;
    this.dataManager = dataManager;
    this.trackingStorage = trackingStorage;
    this.variationConfiguration = variationConfiguration;
    this.requester = requester;
    this.bodyProvider = new BodyProvider(prng);
    this.clientConfiguration = clientConfiguration;

    try {
      this.intervalId = setInterval(() => {
        this.sendDataAll();
      }, trackingInterval);
    } catch (err) {
      this.intervalId && clearInterval(this.intervalId);

      throw err;
    }
    KameleoonLogger.debug`RETURN: new Tracker(dataManager, trackingStorage, variationConfiguration, trackingInterval: ${trackingInterval}, requester, prng, clientConfiguration)`;
  }

  public scheduleVisitor(
    visitorCode: string,
    isConsentProvided: boolean,
  ): void {
    KameleoonLogger.debug`CALL: Tracker.scheduleVisitor(visitorCode: ${visitorCode}, isConsentProvided: ${isConsentProvided})`;
    this.trackingStorage.read().and((trackingData) => {
      trackingData.scheduledVisitors[`_${visitorCode}`] = { isConsentProvided };
      this.trackingStorage.write(trackingData);
    });
    KameleoonLogger.debug`RETURN: Tracker.scheduleVisitor(visitorCode: ${visitorCode}, isConsentProvided: ${isConsentProvided})`;
  }

  private checkIsIdentifier(visitorCode: string): boolean {
    const isUniqueIdentifier = this.dataManager.isUniqueIdentifier(visitorCode);
    const mappingIdentifier =
      this.dataManager.getMappingIdentifier(visitorCode)?.mappingIdentifier ??
      visitorCode;
    let isMappingIdentifier = false;

    // --- Note ---
    // This part is a workaround for Data API
    // If there's is no unique identifier for the visitor
    // We do reconciliation for visitor with himself
    if (isUniqueIdentifier) {
      const linkedVisitor = this.dataManager.getLinkedVisitor(visitorCode);
      const index = this.dataManager.identifierCustomDataIndex;

      if (!linkedVisitor && typeof index === 'number') {
        this.dataManager.storeData(
          visitorCode,
          new CustomData(index, visitorCode),
        );
      } else {
        isMappingIdentifier = true;
      }
    }

    return isMappingIdentifier && visitorCode != mappingIdentifier;
  }

  private getUserAgent(visitorCode: string): string | undefined {
    const targetingData = this.dataManager.getVisitorData(visitorCode);

    if (targetingData) {
      const userAgentData = targetingData[KameleoonData.UserAgent];

      if (userAgentData) {
        return userAgentData.value;
      }
    }

    return undefined;
  }

  public async sendDataAll(): Promise<void> {
    const storageResult = this.trackingStorage.read();

    if (!storageResult.ok) {
      return;
    }

    const { scheduledVisitors } = storageResult.data;
    await this.sendData(scheduledVisitors);
  }

  public async sendDataForVisitor(
    visitorCode: string,
    isConsentProvided: boolean,
  ): Promise<void> {
    const storageResult = this.trackingStorage.read();

    if (!storageResult.ok) {
      return;
    }

    await this.sendData({ [`_${visitorCode}`]: { isConsentProvided } });
  }

  private async sendData(
    scheduledVisitors: ScheduledVisitorsType,
  ): Promise<void> {
    const pendingData: PendingDataType = {};
    const trackingVisitorCodes: string[] = [];

    const trackedVisitors: {
      visitorCode: string;
      isConsentProvided: boolean;
    }[] = [];

    for (const [visitorCode, { isConsentProvided }] of Object.entries(
      scheduledVisitors,
    )) {
      let targetVisitorCode = visitorCode.startsWith('_')
        ? visitorCode.substring(1)
        : visitorCode;

      const isMappingIdentifier = this.checkIsIdentifier(targetVisitorCode);
      const userAgent = this.getUserAgent(targetVisitorCode);

      const visitorData = this.getUnsentVisitorData(
        targetVisitorCode,
        isConsentProvided,
      );
      const variationData = this.getUnsentVariationData(
        targetVisitorCode,
        isConsentProvided,
      );

      const result = this.bodyProvider.addData({
        visitorCode: targetVisitorCode,
        variationData,
        visitorData,
        userAgent,
        isConsentProvided,
        isMappingIdentifier,
      });

      if (result.ok) {
        pendingData[targetVisitorCode] = {
          visitorData,
          variationData,
        };
        trackedVisitors.push({
          visitorCode: targetVisitorCode,
          isConsentProvided,
        });
        trackingVisitorCodes.push(visitorCode);
      } else {
        KameleoonLogger.warning(
          'Visitor data to be tracked exceeded the request size limit. Some visitor data is kept to be sent later.' +
            ' If it is not caused by the peak load, decreasing the tracking interval is recommended.',
        );
        break;
      }
    }

    const { body, hasUserAgent } = this.bodyProvider.getTrackingBody();

    if (trackingVisitorCodes.length !== 0) {
      this.trackingStorage.read().and((trackingData) => {
        trackingVisitorCodes.forEach((visitorCode) => {
          delete trackingData.scheduledVisitors[visitorCode];
        });
        this.trackingStorage.write(trackingData);
      });
    }

    if (!body) {
      return;
    }

    const result = await this.requester.track(body, hasUserAgent);

    if (result.ok) {
      KameleoonLogger.info`Successful request for tracking visitors: ${trackedVisitors}, data: ${pendingData}`;
      this.updateDataStatus(pendingData, TrackingStatus.Sent);
    } else {
      KameleoonLogger.error('Tracking request failed');
      KameleoonLogger.info`Failed request for tracking visitors: ${trackedVisitors}, data: ${pendingData}`;
      this.updateDataStatus(pendingData, TrackingStatus.Unsent);

      let notTrackedVisitors: ScheduledVisitorsType = {};
      for (let { visitorCode, isConsentProvided } of trackedVisitors) {
        visitorCode = `_${visitorCode}`;
        notTrackedVisitors[visitorCode] = {
          isConsentProvided: isConsentProvided,
        };
      }
      this.addNotTrackedVisitors(notTrackedVisitors);
    }
  }

  private addNotTrackedVisitors(
    notTrackedVisitors: ScheduledVisitorsType,
  ): void {
    const storageResult = this.trackingStorage.read();

    if (!storageResult.ok) {
      return;
    }

    const { scheduledVisitors } = storageResult.data;

    Object.entries(notTrackedVisitors).forEach(
      ([visitorCode, { isConsentProvided }]) => {
        if (!scheduledVisitors[visitorCode]) {
          scheduledVisitors[visitorCode] = { isConsentProvided };
        }
      },
    );

    this.trackingStorage.write({
      scheduledVisitors: scheduledVisitors,
    } as TrackingDataType);
  }

  private updateDataStatus(
    data: PendingDataType,
    status: TrackingStatus,
  ): void {
    let updatedData: { [visitorCode: string]: KameleoonDataType[] } = {};

    for (const [visitorCode, { visitorData, variationData }] of Object.entries(
      data,
    )) {
      const visitorDataForUpdate = visitorData
        .filter((dataItem) => !(dataItem instanceof StaticData))
        .map((dataItem) => {
          const item = dataItem as KameleoonDataType;
          item.status = status;
          return item;
        });

      if (visitorDataForUpdate.length) {
        updatedData[visitorCode] = visitorDataForUpdate;
      }

      const experimentIdList = variationData.map((dataItem) =>
        String(dataItem.experimentId),
      );

      this.variationConfiguration.updateDataStatus({
        visitorCode,
        experimentIdList,
        status,
      });
    }

    if (Object.keys(updatedData).length === 0) {
      return;
    }

    if (status === TrackingStatus.Sent) {
      this.dataManager.storeTrackedData(updatedData);
    } else {
      this.dataManager.storeData(updatedData, false);
    }
  }

  private getUnsentVisitorData(
    visitorCode: string,
    isConsentProvided: boolean,
  ): (KameleoonDataType | StaticData)[] {
    const unsentData = this.dataManager.getUnsentData(visitorCode);
    const resultData: (KameleoonDataType | StaticData)[] = [];

    if (!unsentData.length) {
      return [];
    }

    for (const data of unsentData) {
      if (!data.url) {
        continue;
      }
      if (
        !isConsentProvided &&
        (!this.clientConfiguration.hasAnyTargetedDeliveryRule ||
          data instanceof StaticData ||
          data.data.type !== KameleoonData.Conversion)
      ) {
        continue;
      }

      resultData.push(data);
    }

    if (resultData.length) {
      const pendingData: KameleoonDataType[] = resultData
        .filter((data) => !(data instanceof StaticData))
        .map((data) => {
          const item = data as KameleoonDataType;
          item.status = TrackingStatus.Pending;
          return item;
        });

      this.dataManager.storeTrackedData({
        [visitorCode]: pendingData,
      });
    }

    return resultData;
  }

  private getUnsentVariationData(
    visitorCode: string,
    isConsentProvided: boolean,
  ): BaseVariationDataType[] {
    const unsentData = this.variationConfiguration.getUnsentData(visitorCode);
    const sentExperimentIds = new Set<string>();

    if (!unsentData.length) {
      return [];
    }

    const resultData = unsentData.filter((item) => {
      const isIncluded = item.isTargetedRule || isConsentProvided;

      if (isIncluded) {
        sentExperimentIds.add(String(item.experimentId));
      }

      return isIncluded;
    });

    this.variationConfiguration.updateDataStatus({
      visitorCode,
      experimentIdList: Array.from(sentExperimentIds),
      status: TrackingStatus.Pending,
    });

    return resultData;
  }
}
